/**
 * Word-set schema (docs/word-rehearsal/idea.md §11.1): a versioned,
 * shareable JSON document declaring phones, words (ordered phone sequences
 * with roles), and drills. Import never fails over an unknown/uncalibrated
 * sound — an uncalibrated `target` is the expected state, not an error.
 */
export const WORD_SET_SCHEMA_VERSION = 1;
const ROLES = ["target", "scaffold", "optional", "avoid"];
export function validateWordSet(doc) {
    const errors = [];
    const warnings = [];
    if (!doc || typeof doc !== "object") {
        return { ok: false, errors: ["word set must be a JSON object"], warnings };
    }
    const ws = doc;
    if (ws.schemaVersion !== WORD_SET_SCHEMA_VERSION) {
        errors.push(`unsupported schemaVersion ${String(ws.schemaVersion)} ` +
            `(expected ${WORD_SET_SCHEMA_VERSION})`);
    }
    if (typeof ws.id !== "string" || !ws.id.trim())
        errors.push("missing id");
    if (typeof ws.title !== "string")
        errors.push("missing title");
    if (!Array.isArray(ws.phones))
        errors.push("phones must be an array");
    if (!Array.isArray(ws.words))
        errors.push("words must be an array");
    if (errors.length > 0)
        return { ok: false, errors, warnings };
    const phoneIds = new Set();
    for (const p of ws.phones) {
        if (!p || typeof p.id !== "string" || !p.id) {
            errors.push("phone without id");
            continue;
        }
        if (phoneIds.has(p.id))
            errors.push(`duplicate phone id '${p.id}'`);
        phoneIds.add(p.id);
        if (p.kind !== "sustained" && p.kind !== "percussive") {
            errors.push(`phone '${p.id}' has invalid kind '${String(p.kind)}'`);
        }
    }
    for (const p of ws.phones) {
        const refs = [
            ...(p.commonSubstitutions ?? []),
            ...(p.counterFor ?? []),
            ...(p.calibration?.counterLabels ?? []),
        ];
        for (const r of refs) {
            if (!phoneIds.has(r)) {
                warnings.push(`phone '${p.id}' references undeclared substitution '${r}' — ` +
                    `it cannot be trained or suggested`);
            }
        }
    }
    const wordIds = new Set();
    for (const w of ws.words) {
        if (!w || typeof w.id !== "string" || !w.id) {
            errors.push("word without id");
            continue;
        }
        if (wordIds.has(w.id))
            errors.push(`duplicate word id '${w.id}'`);
        wordIds.add(w.id);
        if (typeof w.text !== "string")
            errors.push(`word '${w.id}' missing text`);
        if (!Array.isArray(w.sequence) || w.sequence.length === 0) {
            errors.push(`word '${w.id}' needs a non-empty sequence`);
            continue;
        }
        let targets = 0;
        w.sequence.forEach((e, i) => {
            if (!e || !phoneIds.has(e.phone)) {
                errors.push(`word '${w.id}' references undeclared phone '${e?.phone}'`);
            }
            if (!ROLES.includes(e?.role)) {
                errors.push(`word '${w.id}' entry ${i} has invalid role`);
            }
            if (e?.role === "target") {
                targets++;
                const prev = w.sequence[i - 1];
                const next = w.sequence[i + 1];
                if (prev?.role !== "scaffold" && next?.role !== "scaffold") {
                    warnings.push(`word '${w.id}': target '${e.phone}' has no adjacent scaffold — ` +
                        `it will be hard to segment until the target is calibrated`);
                }
            }
        });
        if (targets === 0)
            errors.push(`word '${w.id}' has no target phone`);
    }
    for (const d of ws.drills ?? []) {
        if (!d || typeof d.id !== "string") {
            errors.push("drill without id");
            continue;
        }
        for (const id of d.wordIds ?? []) {
            if (!wordIds.has(id))
                errors.push(`drill '${d.id}' references unknown word '${id}'`);
        }
        if (!(d.repetitions >= 1))
            errors.push(`drill '${d.id}' needs repetitions ≥ 1`);
    }
    return { ok: errors.length === 0, errors, warnings };
}
export function parseWordSet(json) {
    const parsed = JSON.parse(json);
    const validation = validateWordSet(parsed);
    if (!validation.ok) {
        throw new Error(`invalid word set: ${validation.errors.join("; ")}`);
    }
    if (!Array.isArray(parsed.drills))
        parsed.drills = [];
    return { wordSet: parsed, validation };
}
export function phoneMap(ws) {
    return new Map(ws.phones.map((p) => [p.id, p]));
}
export function wordById(ws, id) {
    return ws.words.find((w) => w.id === id);
}
export function phoneDisplay(p, id) {
    return p?.display ?? (p?.ipa ? `/${p.ipa}/` : id);
}
/** Default per-phone hold used to weight gap splitting when unspecified. */
export function defaultMinHoldMs(p) {
    if (p?.minHoldMs && p.minHoldMs > 0)
        return p.minHoldMs;
    return p?.kind === "percussive" ? 40 : 80;
}
/**
* The classifier label a phone binds to: its library cluster id when
* declared, otherwise a provisional cluster materialized under the phone's
* own id (idea.md §11.1). Phones that opt out (`provision: false`) have
* none and stay `unmapped`.
*/
export function clusterIdOf(p) {
    if (!p)
        return undefined;
    if (p.clusterId)
        return p.clusterId;
    if (p.calibration?.provision === false)
        return undefined;
    return p.id;
}
/** Every declared substitution/counter label for a phone. */
export function counterLabelsOf(p) {
    if (!p)
        return [];
    return [
        ...new Set([
            ...(p.commonSubstitutions ?? []),
            ...(p.calibration?.counterLabels ?? []),
        ]),
    ];
}
